import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Input, Modal, Radio, Select, Space, Tag, Upload } from "antd";
import { DeleteOutlined, PaperClipOutlined, SaveOutlined, SendOutlined, TeamOutlined } from "@ant-design/icons";
import RecipientBrowserModal from "./recipientBrowserModal";
import { Message_type, Target_type } from "./modalMessage";
import { toast } from "sonner";
import { update_message } from "../../api/messages";
import { createFileAttachement, deleteFileAttachementByID, getFileAttachementByEntityId } from "../../api/fileAttachement";
import { Action, buildPermission, buildPermissionContext, Resource } from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";

interface Props {
  visible: boolean;
  onCancel: () => void;
  messageData: any;
  onUpdated?: (updated: any) => void;
}

type SaveMode = "draft" | "send";
type AudienceMode = "custom" | "group";

const emptyRecipients = () => ({ TeacherIds: [], StudentIds: [], ParentIds: [], StaffIds: [], ClassIds: [] });
const groupTargets = [
  Target_type["tous les étudiants"], Target_type["tous les parents"],
  Target_type["tous les enseignants"], Target_type["tout le personnel"],
  Target_type["tous les étudiants et parents"], Target_type["toute l'université"],
];
const groupOptions = [
  { label: "Tous les étudiants", value: Target_type["tous les étudiants"] },
  { label: "Tous les parents", value: Target_type["tous les parents"] },
  { label: "Tous les enseignants", value: Target_type["tous les enseignants"] },
  { label: "Tout le personnel", value: Target_type["tout le personnel"] },
  { label: "Tous les étudiants et leurs parents", value: Target_type["tous les étudiants et parents"] },
  { label: "Toute l’université", value: Target_type["toute l'université"] },
];

const UpdateMessageModal = ({ visible, onCancel, messageData, onUpdated }: Props) => {
  const [form] = Form.useForm();
  const [browserOpen, setBrowserOpen] = useState(false);
  const [audienceMode, setAudienceMode] = useState<AudienceMode>("custom");
  const [recipients, setRecipients] = useState<any>(emptyRecipients);
  const [files, setFiles] = useState<any[]>([]);
  const [existingFiles, setExistingFiles] = useState<any[]>([]);
  const [removedFileIds, setRemovedFileIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const targetType = Form.useWatch("target_type", form);
  const isDraft = Boolean(messageData?.draft);

  const selectedCount = useMemo(
    () => Object.values(recipients).reduce((total: number, ids: any) => total + (ids?.length || 0), 0),
    [recipients],
  );

  useEffect(() => {
    if (!visible || !messageData) return;
    const target = messageData.target_type || messageData.target || Target_type["tous les étudiants"];
    const receivers = messageData.receivers || messageData.MessageRecipients || [];
    const nextRecipients: any = emptyRecipients();
    receivers.forEach((recipient: any) => {
      const id = recipient.receiverId || recipient.receiver_id || recipient.id;
      const type = (recipient.receiver_type || recipient.receiverType || recipient.receiver || "").toUpperCase();
      if (!id) return;
      if (type === "TEACHER") nextRecipients.TeacherIds.push(String(id));
      else if (type === "STUDENT") nextRecipients.StudentIds.push(String(id));
      else if (type === "PARENT") nextRecipients.ParentIds.push(String(id));
      else if (type === "STAFF") nextRecipients.StaffIds.push(String(id));
    });
    const hasExplicitRecipients = receivers.length > 0;
    setRecipients(nextRecipients);
    setAudienceMode(hasExplicitRecipients || !groupTargets.includes(target) ? "custom" : "group");
    form.setFieldsValue({
      target_type: hasExplicitRecipients ? Target_type["sélection personnalisée"] : target,
      message_type: messageData.message_type || Message_type.INFO,
      title: messageData.title || "",
      message: messageData.message || "",
    });
    setFiles([]); setRemovedFileIds([]);
    (async () => {
      try {
        const response = await getFileAttachementByEntityId(
          messageData.id || messageData.messageId, "Message",
          buildPermissionContext(Resource.MESSAGE, Action.UPDATE),
        );
        setExistingFiles(Array.isArray(response.data) ? response.data : []);
      } catch { setExistingFiles([]); }
    })();
  }, [visible, messageData, form]);

  const save = async (mode: SaveMode) => {
    if (!messageData?.id && !messageData?.messageId) return;
    try {
      const values = await form.validateFields();
      if (audienceMode === "custom" && selectedCount === 0) {
        toast.error("Ajoutez au moins un destinataire"); setBrowserOpen(true); return;
      }
      setSubmitting(true);
      const id = messageData.id || messageData.messageId;
      const payload = {
        target: audienceMode === "custom" ? Target_type["sélection personnalisée"] : values.target_type, title: values.title, message: values.message, type: values.message_type,
        studentId: recipients.StudentIds, classId: recipients.ClassIds, parentId: recipients.ParentIds,
        teacherId: recipients.TeacherIds, staffId: recipients.StaffIds,
        message_sent: mode === "send", draft: mode === "draft", archive: false,
      };
      await update_message(id, payload, buildPermissionContext(Resource.MESSAGE, Action.UPDATE));
      for (const fileId of removedFileIds) {
        await deleteFileAttachementByID(fileId, buildPermissionContext(Resource.MESSAGE, Action.UPDATE));
      }
      if (files.length) {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        formData.append("entity_id", id); formData.append("entity_type", "Message");
        await createFileAttachement(formData, buildPermissionContext(Resource.MESSAGE, Action.UPDATE));
      }
      toast.success(mode === "draft" ? "Brouillon mis à jour" : isDraft ? "Message envoyé avec succès" : "Message mis à jour");
      onUpdated?.(payload); onCancel();
    } catch (error: any) {
      if (error?.errorFields) return;
      toast.error(error?.response?.data?.message || error?.response?.data?.error || "Erreur lors de la mise à jour");
    } finally { setSubmitting(false); }
  };

  return (
    <Modal open={visible} title={isDraft ? "Modifier le brouillon" : "Modifier le message"} onCancel={onCancel}
      width={760} destroyOnClose footer={<Space>
        <Button onClick={onCancel} disabled={submitting}>Annuler</Button>
        {isDraft && <Button icon={<SaveOutlined />} onClick={() => save("draft")} loading={submitting}>Enregistrer le brouillon</Button>}
        <Button type="primary" icon={<SendOutlined />} onClick={() => save("send")} loading={submitting}>
          {isDraft ? "Envoyer le message" : "Enregistrer les modifications"}
        </Button>
      </Space>}>
      <Form form={form} layout="vertical">
        <Form.Item label="Destinataires" required>
          <Radio.Group optionType="button" buttonStyle="solid" value={audienceMode} onChange={(event) => {
            const mode: AudienceMode = event.target.value; setAudienceMode(mode);
            if (mode === "custom") form.setFieldValue("target_type", Target_type["sélection personnalisée"]);
            else { setRecipients(emptyRecipients()); form.setFieldValue("target_type", Target_type["tous les étudiants"]); }
          }} options={[{ label: "Personnes ou classes", value: "custom" }, { label: "Groupe entier", value: "group" }]} />
        </Form.Item>
        {audienceMode === "group" ? <Form.Item name="target_type" label="Groupe à contacter" rules={[{ required: true }]}>
          <Select size="large" options={groupOptions} />
        </Form.Item> : <div style={{ marginBottom: 20 }}>
          <Button type="dashed" size="large" block icon={<TeamOutlined />} onClick={() => setBrowserOpen(true)}>
            {selectedCount ? "Modifier les destinataires" : "Ajouter des destinataires"}
          </Button>
          {!!selectedCount && <div style={{ marginTop: 10, padding: 12, background: "#f6f8fb", borderRadius: 8 }}>
            <strong>{selectedCount} sélection{selectedCount > 1 ? "s" : ""}</strong><div style={{ marginTop: 8 }}>
              {!!recipients.ClassIds.length && <Tag color="cyan">{recipients.ClassIds.length} classe(s)</Tag>}
              {!!recipients.StudentIds.length && <Tag color="blue">{recipients.StudentIds.length} étudiant(s)</Tag>}
              {!!recipients.TeacherIds.length && <Tag color="green">{recipients.TeacherIds.length} enseignant(s)</Tag>}
              {!!recipients.ParentIds.length && <Tag color="orange">{recipients.ParentIds.length} parent(s)</Tag>}
              {!!recipients.StaffIds.length && <Tag color="purple">{recipients.StaffIds.length} personnel</Tag>}
            </div></div>}
        </div>}

        <Form.Item name="message_type" label="Priorité" rules={[{ required: true }]}>
          <Radio.Group optionType="button" buttonStyle="solid">
            <Radio.Button value={Message_type.INFO}>Normale</Radio.Button>
            <Radio.Button value={Message_type.WARNING}>Importante</Radio.Button>
            <Radio.Button value={Message_type.URGENT}>Urgente</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="title" label="Objet" rules={[{ required: true, whitespace: true, message: "Veuillez saisir un objet" }]}>
          <Input size="large" placeholder="Objet du message" maxLength={150} showCount />
        </Form.Item>
        <Form.Item name="message" label="Message" rules={[{ required: true, whitespace: true, message: "Le message ne peut pas être vide" }]}>
          <Input.TextArea placeholder="Écrivez votre message…" autoSize={{ minRows: 8, maxRows: 16 }} />
        </Form.Item>

        {!!existingFiles.length && <div style={{ marginBottom: 14 }}><h6>Pièces jointes existantes</h6>
          <Space direction="vertical" style={{ width: "100%" }}>{existingFiles.map((file) => <div key={file.id}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "#f6f8fb", borderRadius: 8 }}>
            <PaperClipOutlined /><span style={{ flex: 1 }}>{file.filename}</span>
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => {
              setExistingFiles((current) => current.filter((item) => item.id !== file.id));
              setRemovedFileIds((current) => [...current, file.id]);
            }}>Retirer</Button>
          </div>)}</Space>
        </div>}
        <Upload.Dragger multiple fileList={files} beforeUpload={(file) => { setFiles((current) => [...current, file]); return false; }}
          onRemove={(file) => setFiles((current) => current.filter((item) => item.uid !== file.uid))}>
          <PaperClipOutlined style={{ fontSize: 22 }} /><div>Ajouter de nouvelles pièces jointes</div>
        </Upload.Dragger>
        {targetType === Target_type["toute l'université"] && <Alert style={{ marginTop: 16 }} type="warning" showIcon message="Envoi général"
          description="Les modifications concernent un message adressé à toute l’université." />}
      </Form>
      <RecipientBrowserModal visible={browserOpen} onCancel={() => setBrowserOpen(false)} recipients={recipients}
        setRecipients={setRecipients} onConfirm={() => { form.setFieldValue("target_type", Target_type["sélection personnalisée"]); setBrowserOpen(false); }} />
    </Modal>
  );
};

export default withPermission(UpdateMessageModal, [buildPermission(Resource.MESSAGE, Action.UPDATE)], "masquer");
